import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/api.config';
import AIBackgroundEffects from './AIBackgroundEffects';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    pendingSubscriptions: 0,
    cancelledSubscriptions: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    agentsReady: 0,
    agentsCreating: 0,
    agentsError: 0,
    emailsSent: 0,
    emailsFailed: 0,
    recentSubscriptions: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(buildApiUrl('/api/admin/dashboard/stats'), {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Cargando estadísticas...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <AIBackgroundEffects step={1} withSidebar={true} />
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p className="header-subtitle">Vista general del sistema</p>
        </div>
        <button className="refresh-btn" onClick={fetchDashboardStats}>
          🔄 Actualizar
        </button>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {/* Suscripciones */}
        <div className="stat-card primary">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>Total Suscripciones</h3>
            <p className="stat-number">{stats.totalSubscriptions}</p>
            <div className="stat-breakdown">
              <span className="stat-badge active">✓ {stats.activeSubscriptions} Activas</span>
              <span className="stat-badge pending">⏳ {stats.pendingSubscriptions} Pendientes</span>
            </div>
          </div>
        </div>

        {/* Ingresos */}
        <div className="stat-card success">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>Ingresos Totales</h3>
            <p className="stat-number">${stats.totalRevenue.toFixed(2)}</p>
            <div className="stat-breakdown">
              <span className="stat-badge">📅 Este mes: ${stats.monthlyRevenue.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Agentes */}
        <div className="stat-card info">
          <div className="stat-icon">🤖</div>
          <div className="stat-content">
            <h3>Agentes de Voz</h3>
            <p className="stat-number">{stats.agentsReady}</p>
            <div className="stat-breakdown">
              <span className="stat-badge active">✓ {stats.agentsReady} Listos</span>
              <span className="stat-badge pending">⚙️ {stats.agentsCreating} Creando</span>
              {stats.agentsError > 0 && (
                <span className="stat-badge error">❌ {stats.agentsError} Error</span>
              )}
            </div>
          </div>
        </div>

        {/* Correos */}
        <div className="stat-card warning">
          <div className="stat-icon">📧</div>
          <div className="stat-content">
            <h3>Correos Enviados</h3>
            <p className="stat-number">{stats.emailsSent}</p>
            <div className="stat-breakdown">
              <span className="stat-badge active">✓ {stats.emailsSent} Exitosos</span>
              {stats.emailsFailed > 0 && (
                <span className="stat-badge error">❌ {stats.emailsFailed} Fallidos</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-section">
        <h2>Suscripciones Recientes</h2>
        <div className="recent-list">
          {stats.recentSubscriptions.length > 0 ? (
            stats.recentSubscriptions.map((sub) => (
              <div key={sub._id} className="recent-item">
                <div className="recent-info">
                  <div className="recent-icon">🏢</div>
                  <div>
                    <h4>{sub.businessName}</h4>
                    <p className="recent-email">{sub.email}</p>
                  </div>
                </div>
                <div className="recent-meta">
                  <span className={`status-badge ${sub.subscriptionStatus}`}>
                    {sub.subscriptionStatus === 'active' && '✓ Activa'}
                    {sub.subscriptionStatus === 'pending' && '⏳ Pendiente'}
                    {sub.subscriptionStatus === 'cancelled' && '❌ Cancelada'}
                  </span>
                  <span className={`agent-badge ${sub.agentStatus}`}>
                    {sub.agentStatus === 'ready' && '🤖 Listo'}
                    {sub.agentStatus === 'creating' && '⚙️ Creando'}
                    {sub.agentStatus === 'error' && '❌ Error'}
                  </span>
                  <span className="recent-date">
                    {new Date(sub.createdAt).toLocaleDateString('es-ES', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No hay suscripciones recientes</p>
            </div>
          )}
        </div>
      </div>

      {/* AI Effects */}
      <div className="dashboard-effects">
        <div className="data-stream stream-1"></div>
        <div className="data-stream stream-2"></div>
        <div className="data-stream stream-3"></div>
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
        <div className="glow-orb orb-3"></div>
      </div>
    </div>
  );
};

export default Dashboard;
